import { useState } from 'react'

import BusinessIcon from '@mui/icons-material/Business'
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'

import { MyListButton } from '@components/MyListButton'
import { Status } from '@components/Status'
import type { Entity } from '@models/Entity'
import {
  allowedDisciplinesForProject,
  canCreateAssignedBijlageForProject,
  getPossibleAssigneesForProject,
  isUserVerantwoordelijkForProject,
} from '@util/project-util'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { ENTITIES } from '../../../entity'
import { Activiteiten } from '../../../entity/activiteiten/components/Activiteiten'
import { useSearchActiviteitenForEntity } from '../../../entity/activiteiten/hooks/useSearchActiviteitenForEntity'
import { AssignedBijlagenByProcesstap } from '../../../entity/bijlagen/components/AssignedBijlagenByProcesstap'
import { useAssignedBijlagenForEntity } from '../../../entity/bijlagen/hooks/useAssignedBijlagenForEntity'
import { useBijlageTemplatesForEntity } from '../../../entity/bijlagen/hooks/useBijlageTemplatesForEntity'
import { useBijlagenForEntity } from '../../../entity/bijlagen/hooks/useBijlagenForEntity'
import { Opdrachten } from '../../../entity/opdrachten/components/Opdrachten'
import { useOpdrachtenForEntity } from '../../../entity/opdrachten/hooks/useOpdrachtenForEntity'
import { LinkedProjects } from '../../../entity/projecten/components/LinkedProjects'
import { useLinkedProjectsForEntity } from '../../../entity/projecten/hooks/useLinkedProjectsForEntity'
import { Subprocessen } from '../../../entity/subprocessen/components/Subprocessen'
import { useSubprocesTemplatesForEntity } from '../../../entity/subprocessen/hooks/useSubprocesTemplatesForEntity'
import { useSubprocessenForEntity } from '../../../entity/subprocessen/hooks/useSubprocessenForEntity'
import { useNavigate } from '../../../router/hooks/useNavigate'
import { useParams } from '../../../router/hooks/useParams'
import { useProjectSelectlists } from '../../../selectlists/hooks/useProjectSelectlists'
import { Page } from '../../../theme/components/Page'
import { PageSection } from '../../../theme/components/PageSection'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { useAansluitingValuelists } from '../../../valuelists/hooks/useAansluitingValuelists'
import { useValuelists } from '../../../valuelists/hooks/useValuelists'
import { useKlantgegevens } from '../../klant/hooks/useKlantgegevens'
import { Klant } from '../../klant/pages/Klant'
import { hasAnyKlantgegevens } from '../../klant/util/has-any-klantgegevens'
import { ProjectGegevensCards } from '../components/ProjectGegevensCards'
import { useProject } from '../hooks/useProject'
import { useProjectLog } from '../hooks/useProjectLog'

import { DisciplineVerzamelingen } from './../components/DisciplineVerzamelingen'
import { Log } from './../components/Log'
import { Objecten } from './../components/Objecten'
import { ProjectOptions } from './../components/options/ProjectOptions'
import { getAannemers } from './../util'
import { generateTabs } from './page-util'

export const Project = () => {
  const { id = '' } = useParams()
  const navigate = useNavigate()

  const [showProjectLog, setShowProjectLog] = useState(false)

  const [anchorEl, setAnchorEl] = useState<HTMLElement>()

  const user = useAuthenticatedUser()
  const {
    canViewActiviteiten,
    canViewKlantgegevens,
    canViewKlantVraagAntwoord,
  } = user

  const { data: project, isError } = useProject({ id })
  const { data: log } = useProjectLog({ id: project?.aanvraagID })

  const incorrectProjectType = project?.type === 'Hoofdleiding'

  const entity: Entity = {
    id: project?.aanvraagID || '',
    type: ENTITIES.PROJECT,
    projecttype: 'Aanvraag',
  }

  const { data: valuelists } = useValuelists()
  const { data: aansluitingValuelists } = useAansluitingValuelists()
  const { data: selectlists } = useProjectSelectlists({ id })
  const { data: subprocesTemplates } = useSubprocesTemplatesForEntity(entity)
  const entityProjects = useLinkedProjectsForEntity(entity)
  const {
    data: opdrachten,
    onChangePage: onChangePageOpdrachten,
    isLoading: isLoadingOpdrachten,
  } = useOpdrachtenForEntity(entity)
  const { data: activiteiten } = useSearchActiviteitenForEntity(entity)
  const { data: assignedBijlagen } = useAssignedBijlagenForEntity(entity)
  const { data: bijlagen } = useBijlagenForEntity(entity)
  const { data: bijlagetemplates } = useBijlageTemplatesForEntity(entity)
  const { data: subprocessen } = useSubprocessenForEntity(entity)
  const { data: gegevens } = useKlantgegevens({ id })

  const hasKlantgegevens = canViewKlantgegevens && hasAnyKlantgegevens(gegevens)

  if (!project || isError || incorrectProjectType || !id) {
    return (
      <Page
        title={['Project', 'Aansluiting']}
        breadcrumbs={[
          { label: 'Aansluiting', href: '/home', tooltip: 'CAPO home' },
          {
            label: 'Projecten',
          },
        ]}
      >
        <PageSection
          id="not-found"
          heading={
            isError || incorrectProjectType || !id
              ? 'Project niet gevonden'
              : 'Project'
          }
        />
      </Page>
    )
  }

  const {
    aanvraagID,
    type,
    linkedProjectdossier,
    lipurl,
    gebiedscode,
    gebiedscodeLabel,
    isActive,
    myList,
  } = project

  const assignees = getPossibleAssigneesForProject({ project })
  const canCreateAssignedBijlage = canCreateAssignedBijlageForProject({
    project,
    user,
    assignees,
  })
  const isVerantwoordelijke = isUserVerantwoordelijkForProject({
    user,
    project,
  })

  const canViewSubprocessen =
    subprocesTemplates.length > 0 || subprocessen.length > 0

  const tabs = generateTabs({
    canViewActiviteiten,
    canViewSubprocessen,
    hasKlantgegevens,
  })

  return (
    <Page
      title={[project.adres, project.aanvraagID, project.type]}
      breadcrumbs={[
        { label: 'Aansluiting', href: '/home', tooltip: 'CAPO home' },
        {
          label: 'Projecten',
          href: '/aansluiting/projecten',
          tooltip: 'Projecten overzicht',
        },
        {
          label: `${project.aanvraagID} (${project.adres})`,
        },
      ]}
      tabs={tabs}
      gap={8}
    >
      <PageSection
        id="gegevens"
        heading={`Project ${aanvraagID}`}
        headingLink={
          lipurl
            ? { href: lipurl, hint: 'Open in mijnaansluiting.nl' }
            : undefined
        }
        actions={
          <Box sx={{ marginLeft: 'auto', flexShrink: 0 }}>
            <MyListButton
              type={'aansluiting'}
              active={myList}
              aanvraagID={aanvraagID}
            />

            {linkedProjectdossier && (
              <StyledIconButton
                title={`Open projectdossier: ${linkedProjectdossier}`}
                onClick={() =>
                  navigate(`/projectdossier/${linkedProjectdossier}`)
                }
                icon={<BusinessIcon />}
              />
            )}

            <Status project={project} placement={'right'} />

            <StyledIconButton
              title={'Project log'}
              onClick={() => setShowProjectLog(!showProjectLog)}
              icon={<InfoOutlineIcon />}
            />

            <StyledIconButton
              title="Opties"
              onClick={(ev) => setAnchorEl(ev.currentTarget)}
              icon={<MoreVertIcon />}
            />
          </Box>
        }
      >
        <ProjectGegevensCards project={project} />
      </PageSection>

      {canViewActiviteiten && (
        <Activiteiten
          activiteiten={activiteiten}
          entity={{
            id: aanvraagID,
            type: ENTITIES.PROJECT,
            gebiedscode,
            gebiedscodeLabel,
            isActive,
            projecttype: type,
          }}
          entityProps={project}
          assignees={assignees}
          isVerantwoordelijke={isVerantwoordelijke}
        />
      )}

      <AssignedBijlagenByProcesstap
        user={user}
        entity={entity}
        assignees={assignees}
        showCreateAssignedBijlage={canCreateAssignedBijlage}
        projectStatus={project.statusText}
        isVerantwoordelijke={isVerantwoordelijke}
        disciplineVerzamelingen={allowedDisciplinesForProject(project)}
        assignedBijlagen={assignedBijlagen}
        bijlagen={bijlagen}
        bijlageTemplates={bijlagetemplates}
      />

      {canViewSubprocessen && (
        <Subprocessen
          project={project}
          entity={entity}
          subprocessen={subprocessen}
          aannemers={getAannemers(project)}
          user={user}
          templates={subprocesTemplates}
          bijlageTemplates={bijlagetemplates}
          assignedBijlagen={assignedBijlagen}
          bijlagen={bijlagen}
        />
      )}

      {hasKlantgegevens && <Klant id={aanvraagID} gegevens={gegevens} />}

      <DisciplineVerzamelingen project={project} />

      <Objecten
        project={project}
        showVraagAntwoord={!!canViewKlantVraagAntwoord}
      />

      <Opdrachten
        opdrachten={opdrachten}
        onChangePage={onChangePageOpdrachten}
        isLoading={isLoadingOpdrachten}
        aanvraagID={aanvraagID}
      />

      {project.type === 'Aanvraag' && (
        <LinkedProjects
          user={user}
          parent={entity}
          projects={entityProjects.data.items}
          projectGroups={entityProjects.data.groups}
          isLoading={entityProjects.isFetching}
          isError={entityProjects.isError}
        />
      )}

      <Log
        open={showProjectLog}
        onClose={() => setShowProjectLog(false)}
        log={log}
        gebieden={aansluitingValuelists.gebieden}
      />

      <ProjectOptions
        project={project}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        gebieden={aansluitingValuelists.gebieden}
        activiteitTemplates={valuelists.activiteitTemplates}
        selectableLabels={selectlists.labels}
      />
    </Page>
  )
}
